/**
 * Integrytas Group - Common Security Module (CSM)
 * use_workspace_layout - single vs. split (side-by-side) workspace layout.
 *
 * Decouples "which tabs are open" (owned by the shell) from "how the workspace is
 * arranged". In single mode one pane shows the focused tab. In split mode the
 * LEFT pane holds one PINNED tab and the RIGHT pane is the workspace for every
 * OTHER open tab (its active one visible). The pinned tab is never shown on the
 * right, so the same tab is never displayed twice. The shell wires tab clicks to
 * focus_code(), the "Split view" action to split_with(pinned, workspace), and tab
 * closes to handle_close(). All state keys are snake_case.
 */

import { useCallback, useState } from 'react';

export type workspace_mode = 'single' | 'split';
export type workspace_pane = 'left' | 'right';

export interface use_workspace_layout_result {
  mode: workspace_mode;
  is_split: boolean;
  active_pane: workspace_pane;
  pane_left_code: string | null;                    // the PINNED tab shown in the left pane
  pane_right_code: string | null;                   // the RIGHT workspace's active (non-pinned) tab
  active_code: string | null;                       // the focused pane's tab code
  pane_code: (pane: workspace_pane) => string | null;
  focus_code: (page_internal_code: string) => void; // focus a tab: the pinned tab focuses the left pane, any other becomes the right pane's active tab
  focus_pane: (pane: workspace_pane) => void;
  split_with: (pinned_code: string, workspace_code: string) => void; // enter split: pinned_code -> left pane, workspace_code -> right workspace
  unsplit: () => void;
  handle_close: (page_internal_code: string) => void;
}

interface layout_state {
  mode: workspace_mode;
  active_pane: workspace_pane;
  left: string | null;
  right: string | null;
}

export function use_workspace_layout(initial_code: string | null): use_workspace_layout_result {
  const [s, set_s] = useState<layout_state>({ mode: 'single', active_pane: 'left', left: initial_code, right: null });

  const focus_code = useCallback((code: string) => {
    set_s(prev => {
      if (prev.mode === 'split') {
        // Clicking the pinned (left) tab focuses the left pane and NEVER copies
        // it into the right workspace — the same tab is never shown twice.
        if (code === prev.left) return prev.active_pane === 'left' ? prev : { ...prev, active_pane: 'left' };
        return { ...prev, right: code, active_pane: 'right' };
      }
      return { ...prev, left: code };
    });
  }, []);

  const focus_pane = useCallback((pane: workspace_pane) => {
    set_s(prev => prev.mode === 'split' ? { ...prev, active_pane: pane } : prev);
  }, []);

  const split_with = useCallback((pinned_code: string, workspace_code: string) => {
    set_s(() => ({ mode: 'split', active_pane: 'right', left: pinned_code, right: workspace_code }));
  }, []);

  const unsplit = useCallback(() => {
    set_s(prev => ({ mode: 'single', active_pane: 'left', left: prev.active_pane === 'right' ? prev.right : prev.left, right: null }));
  }, []);

  const handle_close = useCallback((code: string) => {
    set_s(prev => {
      const left = prev.left === code ? null : prev.left;
      const right = prev.right === code ? null : prev.right;
      if (prev.mode === 'split' && left === null) {
        // the pinned (left) tab closed -> collapse to a single pane on the survivor
        return { mode: 'single', active_pane: 'left', left: right ?? null, right: null };
      }
      // The right workspace's active tab may have closed (right === null); the shell
      // re-focuses a surviving non-pinned tab, or unsplits when <2 tabs remain.
      return { ...prev, left, right };
    });
  }, []);

  const pane_code = useCallback((pane: workspace_pane) => (pane === 'left' ? s.left : s.right), [s.left, s.right]);

  return {
    mode: s.mode,
    is_split: s.mode === 'split',
    active_pane: s.active_pane,
    pane_left_code: s.left,
    pane_right_code: s.right,
    active_code: s.active_pane === 'right' ? s.right : s.left,
    pane_code, focus_code, focus_pane, split_with, unsplit, handle_close,
  };
}
